import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, User } from '../types/auth';

interface AuthStore extends AuthState {
  setAuth: (user: User, token: string, options?: { expiresAt?: string | null; sessionTimeoutMinutes?: number | null }) => void;
  clearAuth: () => void;
  touchSession: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      expiresAt: null,
      sessionTimeoutMinutes: 30,
      lastActivityAt: null,
      setAuth: (user, token, options) =>
        set({
          user,
          token,
          isAuthenticated: true,
          expiresAt: options?.expiresAt === null
            ? null
            : options?.expiresAt ?? new Date(Date.now() + (options?.sessionTimeoutMinutes ?? 30) * 60 * 1000).toISOString(),
          sessionTimeoutMinutes: options?.sessionTimeoutMinutes === null ? null : options?.sessionTimeoutMinutes ?? 30,
          lastActivityAt: new Date().toISOString(),
        }),
      clearAuth: () =>
        set({ user: null, token: null, isAuthenticated: false, expiresAt: null, lastActivityAt: null }),
      touchSession: () =>
        set((state) => {
          if (!state.isAuthenticated) return state;
          return { lastActivityAt: new Date().toISOString() };
        }),
    }),
    {
      name: 'auth-storage',
    }
  )
);

export const useAuth = () => {
  const { user, token, isAuthenticated, expiresAt, sessionTimeoutMinutes, lastActivityAt, setAuth, clearAuth, touchSession } = useAuthStore();
  const expiredByToken = Boolean(expiresAt && Date.now() > new Date(expiresAt).getTime());
  const expiredByIdle = Boolean(
    isAuthenticated &&
      lastActivityAt &&
      sessionTimeoutMinutes &&
      Date.now() - new Date(lastActivityAt).getTime() > sessionTimeoutMinutes * 60 * 1000
  );
  const active = isAuthenticated && !expiredByToken && !expiredByIdle;

  return {
    user,
    token,
    isAuthenticated: active,
    expiresAt,
    sessionTimeoutMinutes,
    lastActivityAt,
    login: setAuth,
    logout: clearAuth,
    touchSession,
    isSessionExpired: expiredByToken || expiredByIdle,
  };
};
